import React, { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  Box,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useTheme, makeStyles } from "@mui/styles";
import { be_regions, lu_regions, fr_regions } from "bundles/common/utils/constants";
import { FormattedMessage, useIntl } from "react-intl";

const SelectRegion = ({
  handleInputValue,
  variant,
  labelId,
  required,
  className = "",
  multiple = false,
  list = false,
  size,
  value,
  allowedRegionCodes,
  ...props
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const beDisplay = allowedRegionCodes ? be_regions.filter((r) => allowedRegionCodes.includes(r)) : be_regions;
  const luDisplay = allowedRegionCodes ? lu_regions.filter((r) => allowedRegionCodes.includes(r)) : lu_regions;
  const frDisplay = allowedRegionCodes ? fr_regions.filter((r) => allowedRegionCodes.includes(r)) : fr_regions;

  const useStyles = makeStyles((theme) => ({
    desktopSelect: {
      border: 0,

      "& fieldset": {
        border: 0,
      },
    },
    mobileSelect: {
      borderRadius: "20px !important",
    },
    menuItem: {
      height: isDesktop ? "36px" : "32px",
      padding: isDesktop ? "0px" : "16px",
      "&.Mui-selected": {
        backgroundColor: "white !important",
      },
      "&:hover": {
        backgroundColor: `${theme.palette.common.sh_lightgrey} !important`,
      },
    },
    menuItemOption: { paddingLeft: "32px !important" },
  }));
  const classes = useStyles();

  const initialBelgiumOptions = {
    country: "belgium",
    regions: be_regions,
    isOpened: true,
  };

  const initialLuxembourgOptions = {
    country: "luxembourg",
    regions: lu_regions,
    isOpened: false,
  };

  const inititialFranceOptions = {
    country: "france",
    regions: fr_regions,
    isOpened: false,
  };

  const [computedBelgiumOptions, setComputedBelgiumOptions] = useState(
    initialBelgiumOptions
  );
  const [computedLuxembourgOptions, setComputedLuxembourgOptions] = useState(
    initialLuxembourgOptions
  );

  const [computedFranceOptions, setComputedFranceOptions] = useState(
    inititialFranceOptions
  );

  const handleToggleCountry = (e, country) => {
    e.stopPropagation();
    if (country == "belgium") {
      setComputedBelgiumOptions({
        ...computedBelgiumOptions,
        isOpened: !computedBelgiumOptions.isOpened,
      });
    } else if (country == "luxembourg") {
      setComputedLuxembourgOptions({
        ...computedLuxembourgOptions,
        isOpened: !computedLuxembourgOptions.isOpened,
      });
    } else if (country == "france") {
      setComputedFranceOptions({
        ...computedFranceOptions,
        isOpened: !computedFranceOptions.isOpened,
      });
    }
  };

  const handleClick = (e) => {
    let values = e.target.value;

    handleUpdate(values);
  };

  const handleMobileInput = (region) => {
    if (value.includes(region)) {
      handleUpdate(value.filter((a) => a != region));
    } else {
      handleUpdate(value.concat(region));
    }
  };

  const handleUpdate = (updatedValues) => {
    let allValues = updatedValues.filter((v) => !v.includes("country"));

    let countryValues = updatedValues.filter((v) => v.includes("country"));
    countryValues.forEach((countryValue) => {
      let country = countryValue.split("-")[1];
      let regions = [];
      if (country == "belgium") {
        if (isAllSelected(beDisplay)) {
          allValues = allValues.filter((v) => !beDisplay.includes(v));
        } else {
          regions = beDisplay;
        }
      } else if (country == "luxembourg") {
        if (isAllSelected(luDisplay)) {
          allValues = allValues.filter((v) => !luDisplay.includes(v));
        } else {
          regions = luDisplay;
        }
      } else if (country == "france") {
        if (isAllSelected(frDisplay)) {
          allValues = allValues.filter((v) => !frDisplay.includes(v));
        } else {
          regions = frDisplay;
        }
      }
      allValues = allValues.concat(regions);
    });

    handleInputValue([...new Set(allValues)]);
  };

  function isAllSelected(countryRegions) {
    return countryRegions.every((region) => value?.includes(region));
  }

  function areSomeSelected(countryRegions) {
    return (
      !isAllSelected(countryRegions) &&
      countryRegions.some((region) => value?.includes(region))
    );
  }

  return !list ? (
    <FormControl
      fullWidth
      required={required || false}
      variant={variant}
      className={className}
      size={size}
    >
      <Select
        {...props}
        value={value || []}
        label={<FormattedMessage id={labelId} />}
        onBlur={handleClick}
        onChange={handleClick}
        multiple={multiple}
        labelId={labelId}
        className={isDesktop ? classes.desktopSelect : classes.mobileSelect}
        IconComponent={() => null}
        inputProps={{ style: { padding: "0 !important" } }}
        MenuProps={{
          style: { maxWidth: 0, maxHeight: 300, position: "absolute" },
          disableScrollLock: true,
          autoFocus: false,
        }}
      >
        {/* BELGIUM */}
        {beDisplay.length > 0 && (
          <>
            <MenuItem
              value="country-belgium"
              label={intl.formatMessage({
                id: "countries.belgium",
              })}
              style={{ display: "flex" }}
              key="country-belgium"
              className={`${classes.menuItem} ${classes.menuItemHeader}`}
            >
              <Checkbox
                checked={isAllSelected(beDisplay)}
                indeterminate={areSomeSelected(beDisplay)}
              />
              <ListItemText
                primary={intl.formatMessage({
                  id: "countries.belgium",
                })}
              />
              <IconButton
                size="small"
                edge="start"
                color="inherit"
                onClick={(e) => handleToggleCountry(e, "belgium")}
                aria-label="close"
              >
                {computedBelgiumOptions.isOpened ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </MenuItem>

            {computedBelgiumOptions.isOpened &&
              beDisplay.map((region) => {
                return (
                  <MenuItem
                    key={region}
                    value={region}
                    className={`${classes.menuItem} ${classes.menuItemOption}`}
                  >
                    <Checkbox checked={value?.includes(region)} />
                    <ListItemText
                      primary={intl.formatMessage({ id: `regions.${region}` })}
                    />
                  </MenuItem>
                );
              })}
          </>
        )}

        {/* FRANCE */}
        {frDisplay.length > 0 && (
          <>
            <MenuItem
              value="country-france"
              label={intl.formatMessage({
                id: "countries.france",
              })}
              style={{ display: "flex" }}
              key="country-france"
              className={`${classes.menuItem} ${classes.menuItemHeader}`}
            >
              <Checkbox
                checked={isAllSelected(frDisplay)}
                indeterminate={areSomeSelected(frDisplay)}
              />
              <ListItemText
                primary={intl.formatMessage({
                  id: "countries.france",
                })}
              />
              <IconButton
                size="small"
                edge="start"
                color="inherit"
                onClick={(e) => handleToggleCountry(e, "france")}
                aria-label="close"
              >
                {computedFranceOptions.isOpened ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </MenuItem>

            {computedFranceOptions.isOpened &&
              frDisplay.map((region) => {
                return (
                  <MenuItem
                    key={region}
                    value={region}
                    className={`${classes.menuItem} ${classes.menuItemOption}`}
                  >
                    <Checkbox checked={value?.includes(region)} />
                    <ListItemText
                      primary={intl.formatMessage({ id: `regions.${region}` })}
                    />
                  </MenuItem>
                );
              })}
          </>
        )}

        {/* LUXEMBOURG */}
        {luDisplay.length > 0 && (
          <>
            <MenuItem
              value="country-luxembourg"
              label={intl.formatMessage({
                id: "countries.luxembourg",
              })}
              style={{ display: "flex" }}
              key="country-luxembourg"
              className={`${classes.menuItem} ${classes.menuItemHeader}`}
            >
              <Checkbox
                checked={isAllSelected(luDisplay)}
                indeterminate={areSomeSelected(luDisplay)}
              />
              <ListItemText
                primary={intl.formatMessage({
                  id: "countries.luxembourg",
                })}
              />
              <IconButton
                size="small"
                edge="start"
                color="inherit"
                onClick={(e) => handleToggleCountry(e, "luxembourg")}
                aria-label="close"
              >
                {computedLuxembourgOptions.isOpened ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </IconButton>
            </MenuItem>

            {computedLuxembourgOptions.isOpened &&
              luDisplay.map((region) => {
                return (
                  <MenuItem
                    key={region}
                    value={region}
                    className={`${classes.menuItem} ${classes.menuItemOption}`}
                  >
                    <Checkbox checked={value?.includes(region)} />
                    <ListItemText
                      primary={intl.formatMessage({ id: `regions.${region}` })}
                    />
                  </MenuItem>
                );
              })}
          </>
        )}
      </Select>
    </FormControl>
  ) : (
    <>
      {beDisplay.length > 0 && (
        <>
          <MenuItem
            onClick={() => handleMobileInput("country-belgium")}
            value="country-belgium"
            label={intl.formatMessage({ id: "countries.belgium" })}
            style={{ display: "flex" }}
            key="country-belgium"
            className={`${classes.menuItem} ${classes.menuItemHeader}`}
          >
            <Checkbox
              checked={isAllSelected(beDisplay)}
              indeterminate={areSomeSelected(beDisplay)}
            />
            <ListItemText primary={intl.formatMessage({ id: "countries.belgium" })} />
            <Box style={{ flexGrow: 50, display: "flex", justifyContent: "end", cursor: "pointer" }} onClick={(e) => handleToggleCountry(e, "belgium")}>
              {computedBelgiumOptions.isOpened ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </MenuItem>
          {computedBelgiumOptions.isOpened && beDisplay.map((region) => (
            <MenuItem key={region} onClick={() => handleMobileInput(region)} value={region} className={`${classes.menuItem} ${classes.menuItemOption}`}>
              <Checkbox checked={value?.includes(region)} />
              <ListItemText primary={intl.formatMessage({ id: `regions.${region}` })} />
            </MenuItem>
          ))}
        </>
      )}
      {frDisplay.length > 0 && (
        <>
          <MenuItem
            onClick={() => handleMobileInput("country-france")}
            value="country-france"
            label={intl.formatMessage({ id: "countries.france" })}
            style={{ display: "flex" }}
            key="country-france"
            className={`${classes.menuItem} ${classes.menuItemHeader}`}
          >
            <Checkbox
              checked={isAllSelected(frDisplay)}
              indeterminate={areSomeSelected(frDisplay)}
            />
            <ListItemText primary={intl.formatMessage({ id: "countries.france" })} />
            <Box style={{ flexGrow: 50, display: "flex", justifyContent: "end", cursor: "pointer" }} onClick={(e) => handleToggleCountry(e, "france")}>
              {computedFranceOptions.isOpened ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </MenuItem>
          {computedFranceOptions.isOpened && frDisplay.map((region) => (
            <MenuItem key={region} onClick={() => handleMobileInput(region)} value={region} className={`${classes.menuItem} ${classes.menuItemOption}`}>
              <Checkbox checked={value?.includes(region)} />
              <ListItemText primary={intl.formatMessage({ id: `regions.${region}` })} />
            </MenuItem>
          ))}
        </>
      )}
      {luDisplay.length > 0 && (
        <>
          <MenuItem
            onClick={() => handleMobileInput("country-luxembourg")}
            value="country-luxembourg"
            label={intl.formatMessage({ id: "countries.luxembourg" })}
            style={{ display: "flex" }}
            key="country-luxembourg"
            className={`${classes.menuItem} ${classes.menuItemHeader}`}
          >
            <Checkbox
              checked={isAllSelected(luDisplay)}
              indeterminate={areSomeSelected(luDisplay)}
            />
            <ListItemText primary={intl.formatMessage({ id: "countries.luxembourg" })} />
          </MenuItem>
          {computedLuxembourgOptions.isOpened && luDisplay.map((region) => (
            <MenuItem key={region} onClick={() => handleMobileInput(region)} value={region} className={`${classes.menuItem} ${classes.menuItemOption}`}>
              <Checkbox checked={value?.includes(region)} />
              <ListItemText primary={intl.formatMessage({ id: `regions.${region}` })} />
            </MenuItem>
          ))}
        </>
      )}
    </>
  );
};

export default SelectRegion;
